import React, { useState, useEffect, useMemo } from "react";
import Basicinfo from "./deviceongoing/Basicinfo";
import Schema from "./deviceongoing/Schema";
import Credentials from "./deviceongoing/Credentials";
import DeviceInventoryReconciliationDialog from "./deviceongoing/DeviceInventoryReconciliationDialog";
import axios from "axios";
import Checkbox from "@mui/material/Checkbox";
import { generateTelemetryCurl } from "./generateTelemetryCurl";
import { generateMqttxCommand } from "./generateMqttxCommand";
import CloseIcon from "@mui/icons-material/Close";

import SearchIcon from "@mui/icons-material/Search";
import Model from "./deviceongoing/Model";

import {
  Box,
  Typography,
  Button,
  TextField,
  Stack,
  Chip,
  Card,
  CardContent,
  Dialog,
  InputAdornment,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  DialogTitle,
  Tooltip,
  LinearProgress,
  Alert,
  MenuItem,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import {
  Add,
  ArrowOutward,
  Download,
  DeviceHub,
  EditSquare,
  Insights,
  Sensors,
  ToggleOff,
  ToggleOn,
  Wifi,
  Close,
} from "@mui/icons-material";
import UpdateModel from "./deviceongoing/UpdateModel";
import { DataGrid } from "@mui/x-data-grid";
import DeviceCard from "../Cards/DeviceCard";
import { useAuth } from "../../auth/useAuth";
import { can } from "../../auth/can";

import { APP_API_BASE } from "../../config/api";
import {
  ONLINE_THRESHOLD_MINUTES,
  ONLINE_THRESHOLD_MS,
  STALE_THRESHOLD_MINUTES,
  STALE_THRESHOLD_MS,
  resolveDeviceStatus,
} from "./deviceStatus";
import { useNotification } from "../../context/NotificationContext";

export {
  ONLINE_THRESHOLD_MINUTES,
  ONLINE_THRESHOLD_MS,
  STALE_THRESHOLD_MINUTES,
  STALE_THRESHOLD_MS,
  resolveDeviceStatus,
};

const API_BASE =
  process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, "") || `${APP_API_BASE}`;
const SEARCH_PLACEHOLDER = "Search devices by ID or name, type, or protocol";
export const resolveDeviceLicenseStatus = (device) => {
  const state = String(device?.license?.state || "NONE").toUpperCase();
  switch (state) {
    case "ACTIVE":
      if ((device?.license?.days_remaining ?? 999) <= 30) {
        return { label: "Expiring Soon", color: "#B45309" };
      }
      return { label: "Licensed", color: "#16A34A" };
    case "EXPIRED":
      return { label: "Expired", color: "#DC2626" };
    case "SUSPENDED":
      return { label: "Suspended", color: "#B45309" };
    case "REVOKED":
      return { label: "Revoked", color: "#7F1D1D" };
    default:
      return { label: "No License", color: "#6B7280" };
  }
};

export function fetchDevices(activeOrg, params = {}, config = {}) {
  const token = localStorage.getItem("authToken");

  if (!token) {
    throw new Error("Auth token missing");
  }

  return axios.get(`${API_BASE}/api/v1/devices`, {
    headers: {
      Authorization: `Bearer ${token}`,
      ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
    },
    params: {
      view: "summary",
      ...params,
    },
    ...config,
  });
}

export default function Devices() {
  const [activeStep, setActiveStep] = useState(0);

  const [activeCredential, setActiveCredential] = useState();
  const [searchText, setSearchText] = useState("");
  const [licenseFilter, setLicenseFilter] = useState("ALL");
  const navigate = useNavigate();
  const { permissions, activeOrg } = useAuth();
  const { showNotification } = useNotification();
  const isPlatformOwner = can(permissions, "system.manage");
  const canViewOrganizations = can(permissions, "organization.view");

  const steps = ["Device_Info", "Device_Schema", "Credentials"];

  const [showCurl, setShowCurl] = useState(false);

  const initialDeviceDraft = {
    basicInfo: {
      name: "",
      inventory_asset_id: "",
      type: "",
      customType: "",
      mqtt_security_mode: "PLAINTEXT",
    },
    schema: [
      { key: "", type: "", customType: "", unit: "", customUnit: "" }, // keep 1 row
    ],
    credentials: {
      device_id: "",
      broker_id: "",
      security_mode: "PLAINTEXT",
      port: "",
      username: "",
      password: "",
      ca_cert_filename: "",
      client_cert_filename: "",
      client_key_filename: "",
      client_cert_identity: "",
    },
  };

  const [deviceDraft, setDeviceDraft] = useState(initialDeviceDraft);

  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openReconciliationModal, setOpenReconciliationModal] = useState(false);
  const [registeredDevices, setRegisteredDevices] = useState([]);
  const [deviceRefreshKey, setDeviceRefreshKey] = useState(0);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [provisionableAssets, setProvisionableAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [assetError, setAssetError] = useState("");

  /* ---------------- handlers ---------------- */

  const [openUpdateModal, setOpenUpdateModal] = useState(false);

  const handleOpenUpdateModal = () => {
    setOpenUpdateModal(true);
  };

  const handleCloseUpdateModal = () => {
    setOpenUpdateModal(false);
  };

  const handleAddDevice = () => {
    setOpenCreateModal(true);
    setActiveStep(0);
    setDeviceDraft(initialDeviceDraft);
  };

  const handleOpenBillingAndLicensing = () => {
    if (!activeOrg?.id) return;
    navigate(`/administration/organizations/${activeOrg.id}?tab=billing`);
  };

  const handleDownloadDevices = async () => {
    let devicesForExport = registeredDevices;

    try {
      const res = await fetchDevices(activeOrg, { view: "full" });
      if (Array.isArray(res.data)) {
        devicesForExport = res.data;
      }
    } catch (err) {
      console.error(
        "Failed to load enriched device export, using current rows.",
        err,
      );
    }

    const rows = devicesForExport.map((device) => ({
      device_id: device.id || "",
      device_name: device.name || "",
      organization_name: device.organization?.name || "",
      organization_code: device.organization?.code || "",
      device_type: device.device_type?.name || "",
      protocol: device.protocol || "",
      is_active: device.is_active ? "Yes" : "No",
      last_seen: device.last_seen || "",
      inventory_asset_serial: device.inventory_asset?.serial_number || "",
      inventory_asset_type: device.inventory_asset?.asset_type || "",
      inventory_asset_model: device.inventory_asset?.model || "",
      created_at: device.created_at || "",
    }));

    if (!rows.length) return;

    const headers = Object.keys(rows[0]);
    const csvLines = [
      headers.join(","),
      ...rows.map((row) =>
        headers
          .map((header) => {
            const value = row[header] ?? "";
            const escaped = String(value).replace(/"/g, '""');
            return `"${escaped}"`;
          })
          .join(","),
      ),
    ];

    const blob = new Blob([csvLines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `devices_${activeOrg?.code || "workspace"}_${new Date()
        .toISOString()
        .slice(0, 10)}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleRowClick = (params) => {
    navigate(`/devices/${params.row.id}`, {
      state: { device: params.row },
    });
  };

  const handleCloseCreateModal = () => {
    setOpenCreateModal(false);
    setActiveStep(0);
    setDeviceDraft(initialDeviceDraft);
  };
  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
  };
  const refreshDevices = () => {
    setDeviceRefreshKey((current) => current + 1);
  };
  // const handleNext = () => setActiveStep((s) => s + 1);
  const handleBack = () => setActiveStep((s) => s - 1);

  const [loading, setLoading] = useState(false);
  const [loadingDevice, setLoadingDevice] = useState(false);
  const [entitlementLoading, setEntitlementLoading] = useState(false);
  const [entitlementError, setEntitlementError] = useState("");
  const [entitlementSummary, setEntitlementSummary] = useState(null);
  const [stepValid, setStepValid] = useState({
    0: false,
    1: false,
    2: true,
  });

  const validateStep = (step, draft) => {
    switch (step) {
      case 0:
        return (
          draft.basicInfo.name.trim() !== "" &&
          draft.basicInfo.inventory_asset_id.trim() !== "" &&
          draft.basicInfo.type.trim() !== ""
        );

      case 1:
        return draft.schema.every(
          (row) => row.key.trim() && row.type.trim() && row.unit.trim(),
        );

      case 2:
        return true;

      default:
        return false;
    }
  };

  const handleNext = async () => {
    setLoading(true);

    // simulate API / async validation
    await new Promise((res) => setTimeout(res, 1000));

    setLoading(false);
    setActiveStep((s) => s + 1);
  };
  const [deviceTypes, setDeviceTypes] = useState([]);

  useEffect(() => {
    setDeviceTypes([]);
  }, [activeOrg?.id]);

  const normalizeMatchText = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  const selectedProvisioningAsset = useMemo(
    () =>
      provisionableAssets.find(
        (asset) => asset.id === deviceDraft.basicInfo.inventory_asset_id,
      ) || null,
    [provisionableAssets, deviceDraft.basicInfo.inventory_asset_id],
  );

  const compatibleDeviceTypes = useMemo(() => {
    if (!selectedProvisioningAsset) return deviceTypes;

    const assetTokens = [
      normalizeMatchText(selectedProvisioningAsset.asset_type),
      normalizeMatchText(selectedProvisioningAsset.model),
    ].filter(Boolean);

    const exactOrNearMatches = deviceTypes.filter((type) => {
      const typeToken = normalizeMatchText(type.name);
      return assetTokens.some(
        (token) =>
          token &&
          typeToken &&
          (typeToken.includes(token) || token.includes(typeToken)),
      );
    });

    return exactOrNearMatches;
  }, [deviceTypes, selectedProvisioningAsset]);

  const deviceTypeHelperText = useMemo(() => {
    if (!selectedProvisioningAsset) {
      return "Choose the logical device family that should drive schema and dashboards.";
    }

    if (compatibleDeviceTypes.length) {
      return "Only device families that match the selected asset are shown here.";
    }

    return `No visible device family matches asset type "${selectedProvisioningAsset.asset_type || "Unknown"}". A CoreData superadmin must allocate or license the correct device type for this organization before provisioning.`;
  }, [selectedProvisioningAsset, compatibleDeviceTypes]);

  const loadProvisionableAssets = async () => {
    if (!activeOrg?.id) {
      setProvisionableAssets([]);
      return;
    }

    const token = localStorage.getItem("authToken");
    if (!token) {
      setProvisionableAssets([]);
      setAssetError("Authentication required");
      return;
    }

    try {
      setAssetsLoading(true);
      setAssetError("");
      const res = await axios.get(`${API_BASE}/api/v1/inventory/assets`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
        },
        params: {
          status: "IN_STOCK",
          per_page: 100,
          page: 1,
        },
      });

      const payload = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
          ? res.data.data
          : [];

      setProvisionableAssets(payload);
    } catch (err) {
      setProvisionableAssets([]);
      setAssetError(
        err?.response?.data?.error ||
          "Failed to load inventory assets for provisioning",
      );
    } finally {
      setAssetsLoading(false);
    }
  };

  const loadEntitlements = async () => {
    if (!activeOrg?.id) {
      setEntitlementSummary(null);
      return;
    }

    const token = localStorage.getItem("authToken");
    if (!token) {
      setEntitlementSummary(null);
      setEntitlementError("Authentication required");
      return;
    }

    try {
      setEntitlementLoading(true);
      setEntitlementError("");
      const res = await axios.get(
        `${API_BASE}/api/v1/licensing/entitlements/current`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
          },
          params: { organization_id: activeOrg.id },
        },
      );
      setEntitlementSummary(res.data?.summary || null);
    } catch (err) {
      setEntitlementSummary(null);
      setEntitlementError(
        err?.response?.data?.error || "Failed to load entitlement status",
      );
    } finally {
      setEntitlementLoading(false);
    }
  };

  const availableSubscriptionSlots =
    entitlementSummary?.available_subscription_slots ?? 0;
  const availableLicenseSlots =
    entitlementSummary?.available_license_slots ?? 0;
  const isPlatformEntitlementExempt = Boolean(
    entitlementSummary?.is_platform_owner_org ||
    entitlementSummary?.entitlement_mode === "platform_exempt",
  );
  const hasProvisioningCapacity =
    isPlatformEntitlementExempt ||
    availableSubscriptionSlots > 0 ||
    availableLicenseSlots > 0;

  useEffect(() => {
    const fetchDeviceTypes = async () => {
      if (!openCreateModal || deviceTypes.length) {
        return;
      }

      const token = localStorage.getItem("authToken");

      if (!token) {
        console.error("Auth token missing");
        return;
      }

      try {
        const res = await axios.get(`${API_BASE}/api/v1/device-types`, {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
          },
          params: {
            view: "summary",
          },
        });

        setDeviceTypes(res.data);
      } catch (err) {
        console.error("Failed to load device types", err);
      } finally {
        // setLoading(false);
      }
    };

    fetchDeviceTypes();
  }, [activeOrg?.id, openCreateModal, deviceTypes.length]); // lazily load for provisioning

  useEffect(() => {
    loadEntitlements();
  }, [activeOrg?.id]);

  useEffect(() => {
    if (openCreateModal) {
      loadProvisionableAssets();
    }
  }, [openCreateModal, activeOrg?.id]);

  useEffect(() => {
    if (!selectedProvisioningAsset) return;

    const hasCurrentType =
      deviceDraft.basicInfo.type &&
      compatibleDeviceTypes.some(
        (type) => type.id === deviceDraft.basicInfo.type,
      );

    const nextType = compatibleDeviceTypes[0]?.id || "";
    const shouldResetType =
      (deviceDraft.basicInfo.type && !hasCurrentType) ||
      (!deviceDraft.basicInfo.type && nextType);

    if (shouldResetType) {
      setDeviceDraft((prev) => ({
        ...prev,
        basicInfo: {
          ...prev.basicInfo,
          type: nextType,
        },
        schema: productSensors[nextType] || [
          {
            key: "",
            type: "",
            customType: "",
            unit: "",
            customUnit: "",
          },
        ],
      }));
    }
  }, [selectedProvisioningAsset, compatibleDeviceTypes]);

  const [error, setError] = useState(null);

  const loadDevices = async (signal) => {
    try {
      setLoadingDevice(true);
      const res = await fetchDevices(activeOrg, {}, signal ? { signal } : {});
      if (res.data) {
        setRegisteredDevices(res.data);
      }
      // setRegisteredDevices(res.data);
    } catch (err) {
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
        return;
      }

      console.error(err);

      if (err.response?.status === 401) {
        setError("Session expired. Please login again.");
        // optional: redirect to login
      } else if (err.response?.status === 403) {
        setError("You do not have permission to view devices.");
      } else {
        setError("Failed to load devices.");
      }
    } finally {
      if (!signal?.aborted) {
        setLoadingDevice(false);
      }
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    loadDevices(controller.signal);
    return () => controller.abort();
  }, [activeOrg?.id, deviceRefreshKey]);

  const handleGetCredentials = async () => {
    try {
      const token = localStorage.getItem("authToken");

      if (!token) {
        console.error("Auth token missing");
        return;
      }

      setLoading(true);

      const payload = {
        name: deviceDraft.basicInfo.name,
        inventory_asset_id: deviceDraft.basicInfo.inventory_asset_id,
        device_type_id:
          deviceDraft.basicInfo.type === "custom"
            ? null
            : deviceDraft.basicInfo.type,
        custom_type:
          deviceDraft.basicInfo.type === "custom"
            ? deviceDraft.basicInfo.customType
            : null,
        mqtt_security_mode: deviceDraft.basicInfo.mqtt_security_mode,
        schema: deviceDraft.schema,
      };

      const response = await axios.post(
        `${API_BASE}/api/v1/devices/${activeOrg.id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
          },
        },
      );

      if (response.data.credentials.mqtt) {
        setActiveCredential(response.data);

        setDeviceDraft((prev) => ({
          ...prev,
          credentials: {
            device_id: response.data.id,
            broker_id: response.data.credentials.mqtt.broker,
            security_mode:
              response.data.credentials.mqtt.security_mode || "PLAINTEXT",
            port: response.data.credentials.mqtt.port || "",
            username: response.data.credentials.mqtt.username,
            password: response.data.credentials.mqtt.password || "",
            ca_cert_filename:
              response.data.credentials.mqtt.ca_cert_filename || "",
            client_cert_filename:
              response.data.credentials.mqtt.client_cert_filename || "",
            client_key_filename:
              response.data.credentials.mqtt.client_key_filename || "",
            client_cert_identity:
              response.data.credentials.mqtt.client_cert_identity || "",
          },
        }));
        setActiveStep((s) => s + 1);
        loadProvisionableAssets();
        refreshDevices();
      }
    } catch (error) {
      console.error("Failed to get credentials", error);
      showNotification(
    error?.response?.data?.error ||
      "Failed to get device credentials. Please try again.",
    "error"
  );

      // optional: show toast / snackbar here
      // enqueueSnackbar("Failed to fetch credentials", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const productSensors = {
    "a1f1a111-1111-4111-8111-111111111111": [
      { key: "rssi", type: "Integer", unit: "dBm" },
      { key: "battery", type: "Integer", unit: "%" },
      { key: "temperature", type: "Float", unit: "°C" },
      { key: "humidity", type: "Integer", unit: "%" },
      { key: "voltage", type: "Float", unit: "V" },
    ],

    "a2f2a222-2222-4222-8222-222222222222": [
      { key: "temperature", type: "Float", unit: "°C" },
      { key: "light", type: "Float", unit: "lx" },
      { key: "battery", type: "Integer", unit: "%" },
      { key: "humidity", type: "Integer", unit: "%" },
      { key: "sound", type: "Float", unit: "dB" },
      { key: "presence", type: "Boolean", unit: "-" },
      { key: "fall_detected", type: "Boolean", unit: "-" },
    ],

    "a3f3a333-3333-4333-8333-333333333333": [
      { key: "solar_voltage", type: "Float", unit: "V" },
      { key: "solar_current", type: "Float", unit: "A" },
      { key: "solar_power", type: "Float", unit: "W" },
      { key: "battery_voltage", type: "Float", unit: "V" },
      { key: "battery_soc", type: "Integer", unit: "%" },
      { key: "energy_generated", type: "Float", unit: "kWh" },
    ],

    "a4f4a444-4444-4444-8444-444444444444": [
      { key: "flow", type: "Float", unit: "L/min" },
      { key: "ph", type: "Float", unit: "pH" },
      { key: "tds", type: "Float", unit: "ppm" },
      { key: "level", type: "Float", unit: "m" },
      { key: "turbidity", type: "Float", unit: "NTU" },
    ],

    "a5f5a555-5555-4555-8555-555555555555": [
      { key: "panic_trigger", type: "Boolean", unit: "-" },
      { key: "sound_level", type: "Float", unit: "dB" },
      { key: "location_accuracy", type: "Float", unit: "m" },
      { key: "battery", type: "Integer", unit: "%" },
      { key: "tamper_detected", type: "Boolean", unit: "-" },
    ],

    "a6f6a666-6666-4666-8666-666666666666": [
      { key: "moisture", type: "Float", unit: "%" },
      { key: "temperature", type: "Float", unit: "°C" },
      { key: "humidity", type: "Integer", unit: "%" },
      { key: "nitrogen", type: "Float", unit: "mg/kg" },
      { key: "phosphorus", type: "Float", unit: "mg/kg" },
      { key: "potassium", type: "Float", unit: "mg/kg" },
      { key: "conductivity", type: "Float", unit: "dS/m" },
      { key: "ph", type: "Float", unit: "pH" },
    ],
    "94fa784d-e24d-41ad-8971-929a45e0389c": [
      { key: "occupancy", type: "Integer", unit: "count" },
    ],
    "7bbcf137-7df3-4448-bc82-fc095a9a6fc7": [
      { key: "pm2_5", type: "String", unit: "µg/m³" },
      { key: "CO₂", type: "Integer", unit: "ppm" },
    ],
    "662ce664-ae2d-48b1-95af-3be1354fbda4": [
      { key: "pm1", type: "Integer", unit: "µg/m³" },
      { key: "temp", type: "float", unit: "°C" },
      { key: "pm25", type: "Integer", unit: "µg/m³" },
      { key: "pm10", type: "Integer", unit: "µg/m³" },
      { key: "co2", type: "Integer", unit: "ppm" },
      { key: "hum", type: "Float", unit: "%" },
      { key: "co", type: "Float", unit: "ppm" },
      { key: "no2", type: "Float", unit: "ppm" },
      { key: "o3", type: "Float", unit: "ppm" },
      { key: "ch2o", type: "Float", unit: "mg/m³" },
      { key: "o2", type: "Float", unit: "%" },
      { key: "so2", type: "Float", unit: "ppm" },
    ],
    "551d58f9-a910-4b49-846d-d760cb6fdbdb": [
      { key: "flow", type: "Float", unit: "L/min" },
      { key: "asset_type", type: "Integer", unit: "m" },
    ],
  };

  const toggleDeviceStatus = async (deviceId, nextState) => {
    setLoading(true);

    const token = localStorage.getItem("authToken");
    if (!token) {
      console.error("Auth token missing");
      setLoading(false);
      return;
    }

    try {
      await axios.patch(
        `${API_BASE}/api/v1/devices/${deviceId}`,
        { is_active: nextState },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
            "Content-Type": "application/json",
          },
        },
      );

      refreshDevices();
      showNotification(
  nextState
    ? "Device enabled successfully."
    : "Device disabled successfully.",
  "success"
);
    } catch (err) {
      if (err.response) {
        console.error("API error:", err.response.data);
        showNotification(
  err.response.data?.error || "Failed to update device state",
  "error"
);
      } else {
        console.error("Network error:", err);
        showNotification("Network error. Please try again.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const safeLower = (value) =>
    value !== null && value !== undefined ? value.toString().toLowerCase() : "";

  const filteredRows = useMemo(() => {
    const query = searchText.toLowerCase();

    return registeredDevices.filter((row) => {
      const matchesSearch =
        !query ||
        safeLower(row.id).includes(query) ||
        safeLower(row.name).includes(query) ||
        safeLower(row.device_type?.name).includes(query) ||
        safeLower(row.protocol).includes(query);

      const matchesLicense =
        licenseFilter === "ALL" ||
        String(row.license?.state || "NONE").toUpperCase() === licenseFilter;

      return matchesSearch && matchesLicense;
    });
  }, [registeredDevices, searchText, licenseFilter]);

  const deviceOverview = useMemo(() => {
    return registeredDevices.reduce(
      (summary, device) => {
        const status = resolveDeviceStatus(device).status;

        summary.total += 1;

        if (status === "online") {
          summary.online += 1;
        } else if (status === "stale") {
          summary.stale += 1;
        } else if (status === "offline") {
          summary.offline += 1;
        }

        if (device.protocol) {
          summary.protocols.add(device.protocol.toUpperCase());
        }

        return summary;
      },
      { total: 0, online: 0, stale: 0, offline: 0, protocols: new Set() },
    );
  }, [registeredDevices]);

  const heroMetrics = [
    {
      label: "Registered devices",
      value: deviceOverview.total,
      icon: <DeviceHub fontSize="small" />,
      tone: "#0F766E",
      bg: "linear-gradient(135deg, rgba(15, 118, 110, 0.18), rgba(45, 212, 191, 0.08))",
    },
    {
      label: "Live right now",
      value: deviceOverview.online,
      icon: <Wifi fontSize="small" />,
      tone: "#166534",
      bg: "linear-gradient(135deg, rgba(34, 197, 94, 0.18), rgba(187, 247, 208, 0.08))",
    },
    {
      label: isPlatformOwner
        ? "Organizations in scope"
        : "Device types in scope",
      value: isPlatformOwner
        ? new Set(
            registeredDevices
              .map(
                (device) => device.organization?.id || device.organization_id,
              )
              .filter(Boolean),
          ).size
        : new Set(
            registeredDevices
              .map((device) => device.device_type?.id || device.device_type_id)
              .filter(Boolean),
          ).size,
      icon: <Sensors fontSize="small" />,
      tone: "#1D4ED8",
      bg: "linear-gradient(135deg, rgba(59, 130, 246, 0.18), rgba(191, 219, 254, 0.08))",
    },
  ];

  const topDeviceTypes = useMemo(() => {
    const counts = new Map();
    registeredDevices.forEach((device) => {
      const key = device.device_type?.name || "Custom Device";
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([label, value]) => ({ label, value }));
  }, [registeredDevices]);

  const topOrganizations = useMemo(() => {
    const counts = new Map();
    registeredDevices.forEach((device) => {
      const key = device.organization?.name || "Unscoped Org";
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([label, value]) => ({ label, value }));
  }, [registeredDevices]);

  const statusSnapshot = isPlatformOwner
    ? [
        { label: "Filtered results", value: filteredRows.length },
        {
          label: "Organizations",
          value: new Set(
            registeredDevices
              .map(
                (device) => device.organization?.id || device.organization_id,
              )
              .filter(Boolean),
          ).size,
        },
        {
          label: "Device types",
          value: new Set(
            registeredDevices
              .map((device) => device.device_type?.id || device.device_type_id)
              .filter(Boolean),
          ).size,
        },
      ]
    : [
        { label: "Filtered results", value: filteredRows.length },
        { label: "Online", value: deviceOverview.online },
        { label: "Stale", value: deviceOverview.stale },
        { label: "Offline", value: deviceOverview.offline },
      ];

  const canUpdateDevice = can(permissions, "device.update");
  const canDeleteDevice = can(permissions, "device.delete");
  const canReconcileInventory = can(permissions, "inventory.binding.view");

  const canShowActionsColumn = canUpdateDevice || canDeleteDevice;
  const columns = [
    {
      field: "sno",
      headerName: "S.No",
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          {params.api.getAllRowIds().indexOf(params.id) + 1}
        </Box>
      ),
    },
    {
      field: "id",
      headerName: "Device ID",
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "name",
      headerName: "Device Name",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "device_type_id",
      headerName: "Device Type",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>
            {params.row.device_type?.name || "Custom Device"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "protocol",
      headerName: "Protocol",
      width: 130,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "created_at",
      headerName: "Created At",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography>{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "license_state",
      headerName: "License",
      width: 170,
      renderCell: (params) => {
        const { label, color } = resolveDeviceLicenseStatus(params.row);
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: color,
              }}
            />
            <Typography variant="body2">{label}</Typography>
          </Box>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 180,
      renderCell: (params) => {
        const { label, color } = resolveDeviceStatus(params.row);
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: color,
              }}
            />
            <Typography variant="body2">{label}</Typography>
          </Box>
        );
      },
    },

    ...(canShowActionsColumn
      ? [
          {
            field: "actions",
            headerName: "Actions",
            width: 180,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
              const row = params.row;
              const isActive = row.is_active;

              return (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center", // ✅ vertical center
                    height: "100%",
                    gap: 1,
                  }}
                >
                  {canUpdateDevice && (
                    <Tooltip
                      title={isActive ? "Disable device" : "Enable device"}
                      arrow
                    >
                      <IconButton
                        size="small"
                        sx={{
                          color: isActive ? "#16A34A" : "#DC2626",
                          "&:hover": {
                            backgroundColor: isActive ? "#DCFCE7" : "#FEE2E2",
                          },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleDeviceStatus(row.id, !isActive);
                        }}
                      >
                        {isActive ? (
                          <ToggleOn disabled={loading} fontSize="small" />
                        ) : (
                          <ToggleOff disabled={loading} fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}

                  {canUpdateDevice && (
                    <Tooltip title="Edit device" arrow>
                      <IconButton
                        size="small"
                        sx={{
                          color: "#9CA3AF",
                          "&:hover": {
                            color: "#448befff",
                            backgroundColor: "#E0E7FF",
                          },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/devices/${row.id}/edit`, {
                            state: {
                              device: row,
                              device_type: deviceTypes,
                            },
                          });
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}

                  {canDeleteDevice && (
                    <Tooltip title="Delete device" arrow>
                      <IconButton
                        size="small"
                        sx={{
                          color: "#9CA3AF",
                          "&:hover": {
                            color: "#EF4444",
                            backgroundColor: "#FEE2E2",
                          },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedDevices(row);
                          setOpenDeleteModal(true);
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              );
            },
          },
        ]
      : []),
  ];

  const curlCommand = generateTelemetryCurl({
    apiBase: API_BASE,
    deviceId: deviceDraft.credentials.device_id,
    token: localStorage.getItem("authToken"),
    schema: deviceDraft.schema,
  });

  const mqttxCommand = generateMqttxCommand({
    broker: activeCredential?.credentials?.mqtt?.broker,
    port: activeCredential?.credentials?.mqtt?.port,
    deviceId: deviceDraft.credentials.device_id,
    authToken: activeCredential?.credentials?.mqtt?.password,
    username: activeCredential?.credentials?.mqtt?.username,
    topic: activeCredential?.credentials?.mqtt?.telemetry_topic,
    schema: activeCredential?.schema,
    securityMode: activeCredential?.credentials?.mqtt?.security_mode,
    caCertFilename: activeCredential?.credentials?.mqtt?.ca_cert_filename,
    clientCertFilename:
      activeCredential?.credentials?.mqtt?.client_cert_filename,
    clientKeyFilename: activeCredential?.credentials?.mqtt?.client_key_filename,
  });

  //   const handleConfirmDelete = async () => {
  //   setConfirmDeleteOpen(false);
  //   await deleteDevice(deviceId, navigate);
  // };

  useEffect(() => {
    setStepValid((prev) => ({
      ...prev,
      [activeStep]: validateStep(activeStep, deviceDraft),
    }));
  }, [deviceDraft, activeStep]);

  useEffect(() => {
    if (openCreateModal) {
      loadEntitlements();
    }
  }, [openCreateModal, activeOrg?.id]);

  const deleteDevice = async (deviceId) => {
    const token = localStorage.getItem("authToken");

    if (!token) {
      alert("Authentication required");
      return;
    }

    try {
      await axios.delete(`${API_BASE}/api/v1/devices/${deviceId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      showNotification("Device deleted successfully.", "success");
    } catch (err) {
      if (err.response) {
        // Backend error (403, 404, etc.)
        showNotification(
  err.response.data?.error || "Failed to delete device",
  "error"
);
      } else {
        // Network / DNS / SSL error
        showNotification("Network error. Please try again.", "error");
      }
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedDevices) return;

    setLoading(true);

    try {
      await deleteDevice(selectedDevices.id);
      setOpenDeleteModal(false);
      refreshDevices();
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          minHeight: "100%",
          pt: { xs: 5, sm: 5, md: 1 },
          pb: 4,
          px: { xs: 0.5, sm: 1, md: 0 },
          background:
            "radial-gradient(circle at top left, rgba(14, 165, 233, 0.14), transparent 30%), radial-gradient(circle at top right, rgba(16, 185, 129, 0.12), transparent 28%), linear-gradient(180deg, #f8fbff 0%, #eef4f8 100%)",
        }}
      >
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            mb: 3,
            px: { xs: 2, md: 3 },
            py: { xs: 2.5, md: 3 },
            borderRadius: 5,
            border: "1px solid rgba(148, 163, 184, 0.18)",
            background:
              "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
            boxShadow: "0 30px 80px rgba(15, 23, 42, 0.18)",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.08) 38%, transparent 60%)",
              transform: "translateX(-35%)",
              pointerEvents: "none",
            }}
          />

          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={3}
            justifyContent="space-between"
            sx={{ position: "relative", zIndex: 1 }}
          >
            <Box sx={{ maxWidth: 720 }}>
              {/* <Chip
                icon={<Insights sx={{ color: "inherit !important" }} />}
                label="Operations console"
                sx={{
                  mb: 2,
                  color: "#DDF4F1",
                  backgroundColor: "rgba(255,255,255,0.12)",
                  border: "1px solid rgba(255,255,255,0.16)",
                }}
              /> */}
              <Typography
                sx={{
                  color: "#F8FAFC",
                  fontSize: { xs: 28, md: 40 },
                  fontWeight: 700,
                  lineHeight: 1.05,
                  letterSpacing: "-0.03em",
                }}
              >
                Device Onboarding
              </Typography>
              <Typography
                sx={{
                  mt: 1.5,
                  maxWidth: 620,
                  color: "rgba(226, 232, 240, 0.86)",
                  fontSize: { xs: 14, md: 16 },
                  lineHeight: 1.7,
                }}
              >
                Manage your Devices with a cleaner operational view, faster
                lookup, and clearer status signals for every connected device.
              </Typography>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                sx={{ mt: 3 }}
              >
                {can(permissions, "device.create") && (
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleAddDevice}
                    sx={{
                      px: 2.2,
                      py: 1.1,
                      borderRadius: 999,
                      textTransform: "none",
                      fontWeight: 700,
                      color: "#082F49",
                      background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
                      boxShadow: "0 16px 30px rgba(245, 158, 11, 0.28)",
                    }}
                  >
                    Add Device
                  </Button>
                )}
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={handleDownloadDevices}
                  disabled={!registeredDevices.length}
                  sx={{
                    px: 2.2,
                    py: 1.1,
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: 700,
                    borderColor: "rgba(148,163,184,0.35)",
                    color: "#0F172A",
                    backgroundColor: "#FFFFFF",
                  }}
                >
                  Download Devices
                </Button>
                {isPlatformOwner && (
                  <Button
                    variant="outlined"
                    endIcon={<ArrowOutward />}
                    onClick={() => navigate("/device_onboarding/devicetypes")}
                    sx={{
                      px: 2.2,
                      py: 1.1,
                      borderRadius: 999,
                      textTransform: "none",
                      fontWeight: 600,
                      color: "#E2E8F0",
                      borderColor: "rgba(226, 232, 240, 0.2)",
                      backgroundColor: "rgba(255,255,255,0.04)",
                    }}
                  >
                    Explore Device Types
                  </Button>
                )}
              </Stack>
            </Box>

            <Stack spacing={1.5} sx={{ minWidth: { lg: 300 } }}>
              {heroMetrics.map((metric) => (
                <Card
                  key={metric.label}
                  sx={{
                    borderRadius: 4,
                    color: "#F8FAFC",
                    background: metric.bg,
                    border: "1px solid rgba(255,255,255,0.12)",
                    backdropFilter: "blur(10px)",
                    boxShadow: "none",
                  }}
                >
                  <CardContent
                    sx={{ px: 2.25, py: 2, "&:last-child": { pb: 2 } }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box>
                        <Typography
                          sx={{ color: "rgba(226,232,240,0.72)", fontSize: 13 }}
                        >
                          {metric.label}
                        </Typography>
                        <Typography
                          sx={{ mt: 0.5, fontSize: 28, fontWeight: 700 }}
                        >
                          {metric.value}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          display: "grid",
                          placeItems: "center",
                          borderRadius: 3,
                          color: metric.tone,
                          backgroundColor: "rgba(255,255,255,0.78)",
                        }}
                      >
                        {metric.icon}
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Stack>
        </Box>

        {can(permissions, "stats.view") && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "stretch",
              mb: 3,
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
            }}
          >
            <DeviceCard />
          </Box>
        )}

        {can(permissions, "device.view") ? (
          <Card
            sx={{
              borderRadius: 5,
              overflow: "hidden",
              border: "1px solid rgba(148, 163, 184, 0.18)",
              boxShadow: "0 24px 60px rgba(15, 23, 42, 0.08)",
            }}
          >
            <Box
              sx={{
                px: { xs: 2, md: 3 },
                py: 2.5,
                borderBottom: "1px solid rgba(226, 232, 240, 0.9)",
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.96), rgba(248,250,252,0.96))",
              }}
            >
              <Stack
                direction={{ xs: "column", lg: "row" }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", lg: "center" }}
              >
                <Box>
                  <Typography
                    sx={{ fontSize: 22, fontWeight: 700, color: "#0F172A" }}
                  >
                    Device Directory
                  </Typography>
                  <Typography sx={{ mt: 0.5, color: "#64748B", fontSize: 14 }}>
                    Search, inspect, and manage registered devices from one
                    place.
                  </Typography>
                </Box>

                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1.25}
                  alignItems={{ xs: "stretch", md: "center" }}
                  sx={{ width: "40%", minWidth: 0 }}
                >
                  {
                    /* <TextField
                    size="small"
                    fullWidth
                    //placeholder="Search by device ID, name type, or protocol"
                    placeholder={SEARCH_PLACEHOLDER}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: "#64748B" }} />
                        </InputAdornment>
                      ), // :white_check_mark: ADD THIS BLOCK (CROSS BUTTON)

                      endAdornment: searchText && (
                        <InputAdornment position="end">
                          <Close
                            onClick={() => setSearchText("")} // :white_check_mark: RESET
                            sx={{
                              cursor: "pointer",
                              color: "#64748B",
                            }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    // sx={{
                    //   minWidth: { md: 360 },
                    //   "& .MuiOutlinedInput-root": {
                    //     borderRadius: 999,
                    //     backgroundColor: "#F8FAFC",
                    //   },
                    // }}
                    sx={{
                      minWidth: 0,
                      width: "100%",

                      "& .MuiOutlinedInput-root": {
                        borderRadius: 999,
                        backgroundColor: "#F8FAFC",
                      },

                      "& .MuiInputBase-input": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                    }}
                  /> */
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Search by device ID, name, type, or protocol"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: "#64748B" }} />
                          </InputAdornment>
                        ),
                        endAdornment: searchText && (
                          <InputAdornment position="end">
                            <Close
                              onClick={() => setSearchText("")}
                              sx={{
                                cursor: "pointer",
                                color: "#64748B",
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        width: "100%",
                        minWidth: 0,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 999,
                          backgroundColor: "#F8FAFC",
                        },
                        "& .MuiInputBase-input": {
                          minWidth: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        },
                      }}
                    />
                  }
                  <TextField
                    size="small"
                    select
                    value={licenseFilter}
                    onChange={(e) => setLicenseFilter(e.target.value)}
                    sx={{
                      minWidth: { md: 190 },
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 999,
                        backgroundColor: "#F8FAFC",
                      },
                    }}
                  >
                    <MenuItem value="ALL">All Licenses</MenuItem>
                    <MenuItem value="ACTIVE">Licensed</MenuItem>
                    <MenuItem value="EXPIRED">Expired</MenuItem>
                    <MenuItem value="SUSPENDED">Suspended</MenuItem>
                    <MenuItem value="REVOKED">Revoked</MenuItem>
                    <MenuItem value="NONE">No License</MenuItem>
                  </TextField>
                  {/* <Button
                    variant="outlined"
                    onClick={() => setSearchText("")}
                    sx={{
                      minWidth: 110,
                      borderRadius: 999,
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Reset
                  </Button> */}
                </Stack>
              </Stack>

              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
                sx={{ mt: 2 }}
              >
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {statusSnapshot.map((item) => (
                    <Chip
                      key={item.label}
                      label={`${item.label}: ${item.value}`}
                      sx={{
                        borderRadius: 999,
                        backgroundColor: "#E2E8F0",
                        color: "#0F172A",
                        fontWeight: 600,
                      }}
                    />
                  ))}
                </Stack>
                {loadingDevice && (
                  <Box sx={{ width: { xs: "100%", md: 180 } }}>
                    <LinearProgress
                      sx={{
                        height: 8,
                        borderRadius: 999,
                        backgroundColor: "#E2E8F0",
                      }}
                    />
                  </Box>
                )}
              </Stack>

              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 1.5 }}
              >
                {(isPlatformOwner ? topOrganizations : topDeviceTypes).map(
                  (item) => (
                    <Chip
                      key={item.label}
                      label={`${item.label}: ${item.value}`}
                      sx={{
                        borderRadius: 999,
                        backgroundColor: "#F8FAFC",
                        color: "#334155",
                        border: "1px solid rgba(148,163,184,0.18)",
                        fontWeight: 700,
                      }}
                    />
                  ),
                )}
              </Stack>

              {activeOrg?.id && canViewOrganizations && (
                <Card
                  elevation={0}
                  sx={{
                    mt: 2,
                    borderRadius: 3,
                    border: "1px solid rgba(148, 163, 184, 0.18)",
                    background: hasProvisioningCapacity
                      ? "linear-gradient(135deg, rgba(240,253,250,0.96), rgba(236,253,245,0.96))"
                      : "linear-gradient(135deg, rgba(254,242,242,0.98), rgba(255,247,237,0.98))",
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Stack
                      direction={{ xs: "column", lg: "row" }}
                      spacing={1.5}
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", lg: "center" }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            fontWeight: 800,
                            color: hasProvisioningCapacity
                              ? "#065F46"
                              : "#991B1B",
                          }}
                        >
                          Entitlement Readiness
                        </Typography>
                        <Typography
                          sx={{ mt: 0.5, color: "#64748B", fontSize: 14 }}
                        >
                          Provisioning uses either active subscription capacity
                          or an available license pool from the current
                          workspace.
                        </Typography>
                      </Box>

                      <Stack
                        direction="row"
                        spacing={1}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        {isPlatformEntitlementExempt ? (
                          <Chip
                            label="Platform owner exemption active"
                            sx={{
                              borderRadius: 999,
                              fontWeight: 700,
                              color: "#065F46",
                              backgroundColor: "#D1FAE5",
                            }}
                          />
                        ) : (
                          <>
                            <Chip
                              label={`Subscription slots left: ${availableSubscriptionSlots}`}
                              sx={{
                                borderRadius: 999,
                                fontWeight: 700,
                                color: "#1D4ED8",
                                backgroundColor: "#DBEAFE",
                              }}
                            />
                            <Chip
                              label={`License slots left: ${availableLicenseSlots}`}
                              sx={{
                                borderRadius: 999,
                                fontWeight: 700,
                                color: "#92400E",
                                backgroundColor: "#FEF3C7",
                              }}
                            />
                          </>
                        )}
                        {!hasProvisioningCapacity && (
                          <Button
                            variant="outlined"
                            onClick={handleOpenBillingAndLicensing}
                            sx={{
                              borderRadius: 999,
                              textTransform: "none",
                              fontWeight: 700,
                            }}
                          >
                            Open Billing & Licensing
                          </Button>
                        )}
                      </Stack>
                    </Stack>

                    {entitlementLoading ? (
                      <LinearProgress sx={{ mt: 1.75, borderRadius: 999 }} />
                    ) : entitlementError ? (
                      <Alert
                        severity="warning"
                        sx={{ mt: 1.75, borderRadius: 3 }}
                      >
                        {entitlementError}
                      </Alert>
                    ) : isPlatformEntitlementExempt ? (
                      <Alert severity="info" sx={{ mt: 1.75, borderRadius: 3 }}>
                        CoreData platform-owner workspaces are exempt from
                        subscription and license capacity checks.
                      </Alert>
                    ) : !hasProvisioningCapacity ? (
                      <Alert
                        severity="error"
                        sx={{ mt: 1.75, borderRadius: 3 }}
                      >
                        This organization has no available provisioning capacity
                        right now. Add a subscription or a license pool before
                        registering a new device.
                      </Alert>
                    ) : (
                      <Alert
                        severity="success"
                        sx={{ mt: 1.75, borderRadius: 3 }}
                      >
                        The current workspace can provision devices immediately.
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              )}
            </Box>

            <Box
              sx={{
                px: { xs: 1, md: 2 },
                pb: 1.5,
                pt: 1,
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.98))",
              }}
            >
              <Box
                sx={{
                  mt: 1,
                  height: { xs: 480, md: 560 },
                  borderRadius: 4,
                  overflowX: "auto",
                  overflowY: "hidden",
                  border: "1px solid rgba(226, 232, 240, 0.9)",
                  backgroundColor: "#FFFFFF",
                }}
              >
                <DataGrid
                  rows={filteredRows}
                  columns={columns}
                  density="compact"
                  loading={loadingDevice}
                  initialState={{
                    pagination: {
                      paginationModel: { page: 0, pageSize: 20 },
                    },
                  }}
                  pageSizeOptions={[15, 20, 25]}
                  disableColumnMenu
                  onRowClick={handleRowClick}
                  sx={{
                    border: "none",
                    backgroundColor: "#FFF",
                    minWidth: 900,
                    "& .MuiDataGrid-columnHeaders": {
                      background:
                        "linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)",
                      borderBottom: "1px solid rgba(226, 232, 240, 0.95)",
                      color: "#334155",
                      fontWeight: 700,
                      fontSize: 13,
                      letterSpacing: "0.02em",
                      textTransform: "uppercase",
                    },
                    "& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell": {
                      outline: "none !important",
                    },
                    "& .MuiDataGrid-row": {
                      borderBottom: "1px solid rgba(241, 245, 249, 0.9)",
                      transition:
                        "background-color 180ms ease, transform 180ms ease",
                    },
                    "& .MuiDataGrid-row:hover": {
                      backgroundColor: "#F8FAFC",
                      cursor: "pointer",
                    },
                    "& .MuiDataGrid-row.Mui-selected": {
                      backgroundColor: "rgba(14, 165, 233, 0.08)",
                    },
                    "& .MuiDataGrid-cell": {
                      color: "#0F172A",
                      py: 1.2,
                      minWidth: 0,
                    },
                    "& .MuiDataGrid-footerContainer": {
                      borderTop: "1px solid rgba(226, 232, 240, 0.9)",
                      backgroundColor: "#FCFDFE",
                    },
                  }}
                  localeText={{
                    noRowsLabel: "No devices registered yet",
                  }}
                />
              </Box>
            </Box>
          </Card>
        ) : (
          <Card
            sx={{
              mt: 4,
              borderRadius: 5,
              border: "1px solid rgba(251, 191, 36, 0.28)",
              background:
                "linear-gradient(135deg, rgba(255, 251, 235, 0.96), rgba(255, 247, 237, 0.96))",
              boxShadow: "0 24px 60px rgba(120, 53, 15, 0.08)",
            }}
          >
            <CardContent sx={{ py: 6, textAlign: "center" }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: "#92400E" }}
              >
                Access Restricted
              </Typography>
              <Typography color="#9A3412" sx={{ mt: 1 }}>
                You do not have permission to view devices.
              </Typography>
            </CardContent>
          </Card>
        )}

        {error && (
          <Card
            sx={{
              mt: 3,
              borderRadius: 4,
              border: "1px solid rgba(248, 113, 113, 0.22)",
              backgroundColor: "#FEF2F2",
            }}
          >
            <CardContent sx={{ py: 2 }}>
              <Typography sx={{ color: "#B91C1C", fontWeight: 600 }}>
                {error}
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* ================= Register Device Modal ================= */}
      <Dialog
        open={openCreateModal}
        onClose={handleCloseCreateModal}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 6,
            overflow: "hidden",
            border: "1px solid rgba(148, 163, 184, 0.18)",
            boxShadow: "0 30px 90px rgba(15, 23, 42, 0.22)",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            px: { xs: 2.5, md: 3.5 },
            py: 3,
            background:
              "linear-gradient(135deg, rgba(15, 23, 42, 0.98), rgba(3, 105, 161, 0.92) 58%, rgba(15, 118, 110, 0.88))",
          }}
        >
          <IconButton
            onClick={handleCloseCreateModal}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              color: "rgba(248, 250, 252, 0.75)",
              "&:hover": {
                color: "#F8FAFC",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Chip
            label="Provisioning Flow"
            sx={{
              mb: 1.5,
              color: "#DDF4F1",
              backgroundColor: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.16)",
              fontWeight: 700,
            }}
          />
          <Typography
            fontWeight={800}
            fontSize={{ xs: 30, md: 38 }}
            sx={{ color: "#F8FAFC" }}
          >
            Device Provisioning
          </Typography>
          <Typography
            sx={{
              mt: 1,
              maxWidth: 640,
              color: "rgba(226,232,240,0.82)",
              lineHeight: 1.7,
            }}
          >
            Follow a structured onboarding flow to define the device, shape its
            telemetry contract, and generate production-ready credentials.
          </Typography>
        </Box>

        <Box
          sx={{
            px: { xs: 2.5, md: 3.5 },
            pt: 3,
            pb: 1,
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.98))",
          }}
        >
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{
              overflowX: "auto",
              pb: 1,
              "&::-webkit-scrollbar": { height: 6 },
              "& .MuiStepLabel-label": {
                mt: 1,
                fontWeight: 700,
                color: "#64748B",
                fontSize: { xs: 12, sm: 14 },
              },
              "& .MuiStepLabel-label.Mui-active": {
                color: "#0F172A",
              },
              "& .MuiStepLabel-label.Mui-completed": {
                color: "#0F766E",
              },
              "& .MuiStepConnector-line": {
                borderColor: "rgba(203,213,225,0.95)",
                borderTopWidth: 2,
              },
              "& .MuiStepIcon-root": {
                color: "#CBD5E1",
              },
              "& .MuiStepIcon-root.Mui-active": {
                color: "#0EA5E9",
              },
              "& .MuiStepIcon-root.Mui-completed": {
                color: "#14B8A6",
              },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label.replaceAll("_", " ")}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <DialogContent
          sx={{
            px: { xs: 2.5, md: 3.5 },
            py: 3,
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.98))",
          }}
        >
          <Card
            elevation={0}
            sx={{
              mb: 3,
              borderRadius: 4,
              border: "1px solid rgba(148, 163, 184, 0.16)",
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.98), rgba(248,250,252,0.98))",
            }}
          >
            <CardContent sx={{ p: 2.25 }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                spacing={2}
              >
                <Box>
                  <Typography sx={{ fontWeight: 800, color: "#111827" }}>
                    Subscription & License Check
                  </Typography>
                  <Typography sx={{ mt: 0.5, color: "#64748B", fontSize: 14 }}>
                    Provisioning now follows the inventory-first flow: choose an
                    in-stock asset, then the platform binds and provisions it as
                    one atomic action.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {isPlatformEntitlementExempt ? (
                    <Chip
                      label="Platform owner exemption"
                      sx={{
                        fontWeight: 700,
                        color: "#065F46",
                        backgroundColor: "#D1FAE5",
                      }}
                    />
                  ) : (
                    <>
                      <Chip
                        label={`Subscription slots: ${availableSubscriptionSlots}`}
                        sx={{
                          fontWeight: 700,
                          color: "#1D4ED8",
                          backgroundColor: "#DBEAFE",
                        }}
                      />
                      <Chip
                        label={`License slots: ${availableLicenseSlots}`}
                        sx={{
                          fontWeight: 700,
                          color: "#92400E",
                          backgroundColor: "#FEF3C7",
                        }}
                      />
                    </>
                  )}
                </Stack>
              </Stack>

              {entitlementLoading ? (
                <LinearProgress sx={{ mt: 2, borderRadius: 999 }} />
              ) : assetsLoading ? (
                <LinearProgress sx={{ mt: 2, borderRadius: 999 }} />
              ) : entitlementError ? (
                <Alert severity="warning" sx={{ mt: 2, borderRadius: 3 }}>
                  {entitlementError}
                </Alert>
              ) : assetError ? (
                <Alert severity="warning" sx={{ mt: 2, borderRadius: 3 }}>
                  {assetError}
                </Alert>
              ) : isPlatformEntitlementExempt ? (
                <Alert severity="info" sx={{ mt: 2, borderRadius: 3 }}>
                  This CoreData platform-owner workspace is exempt from
                  subscription and license checks.
                </Alert>
              ) : !provisionableAssets.length ? (
                <Alert severity="warning" sx={{ mt: 2, borderRadius: 3 }}>
                  No in-stock inventory assets are available in this workspace.
                  Only assets in <strong>IN_STOCK</strong> state can be
                  provisioned. If an asset was transferred but is still deployed
                  or bound to another device, it will appear in inventory but
                  not in this dropdown until it is unbound and returned to
                  stock.
                </Alert>
              ) : hasProvisioningCapacity ? (
                <Alert severity="success" sx={{ mt: 2, borderRadius: 3 }}>
                  This organization can provision devices right now from its
                  available inventory and entitlement pool.
                </Alert>
              ) : (
                <Alert
                  severity="error"
                  sx={{ mt: 2, borderRadius: 3 }}
                  action={
                    canViewOrganizations && activeOrg?.id ? (
                      <Button
                        color="inherit"
                        size="small"
                        onClick={handleOpenBillingAndLicensing}
                        sx={{ fontWeight: 700, textTransform: "none" }}
                      >
                        Open Billing
                      </Button>
                    ) : null
                  }
                >
                  No subscription or license capacity is available for this
                  organization. Add a subscription or license pool before
                  provisioning a new device.
                </Alert>
              )}
            </CardContent>
          </Card>

          {activeStep === 0 && (
            <Basicinfo
              deviceTypes={compatibleDeviceTypes}
              provisionableAssets={provisionableAssets}
              selectedAsset={selectedProvisioningAsset}
              deviceTypeHelperText={deviceTypeHelperText}
              assetsLoading={assetsLoading}
              assetError={assetError}
              allowCustomType={isPlatformOwner}
              allowSecurityModeOverride={isPlatformOwner}
              value={deviceDraft.basicInfo}
              onChange={(data) => {
                setDeviceDraft((prev) => ({
                  ...prev,
                  basicInfo: data,
                  schema: productSensors[data.type] || [
                    {
                      key: "",
                      type: "",
                      customType: "",
                      unit: "",
                      customUnit: "",
                    },
                  ],
                }));
              }}
            />
          )}

          {activeStep === 1 && (
            <Schema
              value={deviceDraft.schema}
              onChange={(data) =>
                setDeviceDraft((prev) => ({ ...prev, schema: data }))
              }
            />
          )}

          {activeStep === 2 && (
            <Credentials
              value={deviceDraft.credentials}
              showCurl={showCurl}
              curlCommand={curlCommand}
              title="MQTTX Test Command (Copy & Paste)"
              mqttxCommand={mqttxCommand}
              onChange={(data) =>
                setDeviceDraft((prev) => ({ ...prev, credentials: data }))
              }
            />
          )}
        </DialogContent>

        {/* Footer Actions */}
        <DialogActions
          sx={{
            px: { xs: 2.5, md: 3.5 },
            py: 2.5,
            justifyContent: "space-between",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: 1.25,
            borderTop: "1px solid rgba(226, 232, 240, 0.9)",
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.98))",
          }}
        >
          <Button
            onClick={handleCloseCreateModal}
            sx={{
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 700,
              color: "#475569",
            }}
          >
            Cancel
          </Button>

          <Stack
            direction={{ xs: "column-reverse", sm: "row" }}
            spacing={1.25}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            {activeStep == 1 && (
              <Button
                onClick={handleBack}
                sx={{
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 700,
                  color: "#475569",
                }}
              >
                Back
              </Button>
            )}

            {activeStep === 0 ? (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!stepValid[0] || loading}
                sx={{
                  minWidth: { sm: 170 },
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 700,
                  color: "#082F49",
                  background: "linear-gradient(135deg, #FDE68A, #F59E0B)",
                  boxShadow: "0 16px 30px rgba(245, 158, 11, 0.22)",
                  cursor: "pointer",
                }}
              >
                {loading ? (
                  <CircularProgress size={22} sx={{ color: "#fff" }} />
                ) : (
                  "Next"
                )}
              </Button>
            ) : activeStep === 1 ? (
              <Button
                variant="contained"
                onClick={handleGetCredentials}
                disabled={
                  !stepValid[1] ||
                  loading ||
                  entitlementLoading ||
                  assetsLoading ||
                  !provisionableAssets.length ||
                  !hasProvisioningCapacity
                }
                sx={{
                  minWidth: { sm: 190 },
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 700,
                  background: "linear-gradient(135deg, #0EA5E9, #14B8A6)",
                  boxShadow: "0 16px 30px rgba(20, 184, 166, 0.18)",
                }}
              >
                {loading ? (
                  <CircularProgress size={22} sx={{ color: "#fff" }} />
                ) : (
                  "Get Credentials"
                )}
              </Button>
            ) : (
              !showCurl && (
                <Button
                  disabled={loading}
                  variant="contained"
                  sx={{
                    minWidth: { sm: 190 },
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: 700,
                    background: "linear-gradient(135deg, #0EA5E9, #2563EB)",
                    boxShadow: "0 16px 30px rgba(37, 99, 235, 0.18)",
                  }}
                  onClick={() => setShowCurl(true)}
                >
                  {loading ? (
                    <CircularProgress size={22} sx={{ color: "#fff" }} />
                  ) : (
                    "Register Device"
                  )}
                </Button>
              )
            )}
          </Stack>
        </DialogActions>
      </Dialog>
      <Dialog open={openDeleteModal} onClose={handleCloseDeleteModal}>
        <DialogTitle>Confirm Changes</DialogTitle>

        <DialogContent>
          <Typography>Are you sure you want to Delete this?</Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDeleteModal}>Cancel</Button>

          <Button
            variant="contained"
            disabled={loading}
            onClick={handleConfirmDelete}
          >
            {loading ? (
              <CircularProgress size={22} sx={{ color: "#fff" }} />
            ) : (
              "Yes, Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <DeviceInventoryReconciliationDialog
        open={openReconciliationModal}
        onClose={() => setOpenReconciliationModal(false)}
        activeOrg={activeOrg}
        onSuccess={() => {
          refreshDevices();
          loadProvisionableAssets();
        }}
      />
    </>
  );
}
